import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { OrganizationSiteDto } from '../../interfaces/organization-site-dto';
import { OrganizationStatus } from '../../enums/organization-status-enum';
import { OrganizationSearchService } from '../../services/organization-search.service';
import { OrganizationSiteFilterDto } from '../../interfaces/organization-site-filter-dto';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import {
    EmptyStateComponent, FormatDateArrayPipe, OrDashPipe, PaginationComponent,
    OrganizationDto,
    OrganizationRelocationService, OrganizationRelocationStatus, OrganizationRelocationStatusDto
} from 'lib';
import { OrganizationMigrationService } from '../../services/organization-migration.service';
import { AddToOrganizationComponent } from '../add-to-organization/add-to-organization.component';
import { OrganizationRelocationComponent } from '../organization-relocation/organization-relocation.component';
import { PreconfigureMigrationModalComponent } from '../preconfigure-migration-modal/preconfigure-migration-modal.component';

const relocationInProgressStatuses: ReadonlySet<OrganizationRelocationStatus> = new Set([
    OrganizationRelocationStatus.PENDING_SOURCE_ACCEPTANCE,
    OrganizationRelocationStatus.PENDING_TARGET_ACCEPTANCE,
    OrganizationRelocationStatus.ACCEPTED,
    OrganizationRelocationStatus.QUEUED,
    OrganizationRelocationStatus.RELOCATING
]);

@Component({
    selector: 'mmoga-organization-site-table',
    standalone: true,
    imports: [
        TableModule,
        PaginationComponent,
        FaIconComponent,
        CommonModule,
        OrDashPipe,
        EmptyStateComponent,
        AddToOrganizationComponent,
        OrganizationRelocationComponent,
        FormatDateArrayPipe,
        PreconfigureMigrationModalComponent
    ],
    templateUrl: './organization-site-table.component.html',
    styleUrl: './organization-site-table.component.scss'
})
export class OrganizationSiteTableComponent implements OnInit, OnDestroy {
    private readonly organizationSearchService = inject(OrganizationSearchService);
    private readonly organizationMigrationService = inject(OrganizationMigrationService);
    private readonly organizationRelocationService = inject(OrganizationRelocationService);

    @Input() searchFilterObs: Observable<OrganizationSiteFilterDto> = new Observable<OrganizationSiteFilterDto>();
    @Input() searchTermObs: Observable<string> = new Observable<string>();

    totalItems = 0;
    isLoading = true;
    tableData!: OrganizationSiteDto[];
    searchFilter: OrganizationSiteFilterDto = {} as OrganizationSiteFilterDto;
    showAddToOrganizationModal = false;
    showMoveOrganizationModal = false;
    showPreconfigureModal = false;
    preconfigureFiltersSnapshot: OrganizationSiteFilterDto | undefined;
    existingPreconfigureDate: Date | null = null;
    existingEnablementDate: Date | null = null;
    canSetPreconfigureDate = false;
    canSetEnablementDate = true;
    selectedOrganizationSite: OrganizationSiteDto | undefined;
    selectedIds = new Set<number>();
    preconfigureEligibleSelectedIds = new Set<number>();
    allPagesSelected = false;
    relocatingOrganizationIds = new Set<number>();
    preconfigureExcludedCount = 0;
    private searchFilterSubscription = new Subscription();
    private searchTermSubscription = new Subscription();
    protected readonly faInfoCircle = faInfoCircle;
    protected readonly fullyEnabledTooltip = 'This site is fully enabled and cannot be modified.';

    constructor() {
        this.searchFilter = this.getDefaultFilter();
    }

    ngOnInit() {
        this.searchFilterSubscription = this.searchFilterObs.subscribe(data => {
            this.searchFilter = { searchTerm: this.searchFilter?.searchTerm, ...this.getDefaultFilter(), ...data };
            this.clearSelection();
            this.loadOrganizations();
        });
        this.searchTermSubscription = this.searchTermObs.subscribe(term => {
            this.searchFilter = { ...this.searchFilter, ...this.getDefaultFilter(), searchTerm: term };
            this.clearSelection();
            this.loadOrganizations();
        });
        this.loadOrganizations();
    }

    loadOrganizations(): void {
        this.organizationSearchService.searchAllOrganizations(this.searchFilter)
            .subscribe({
                next: (response) => {
                    this.tableData = response.content;
                    this.totalItems = response.totalElements;
                    this.isLoading = false;
                    this.revalidateEligibilityForCurrentPage();
                    this.loadRelocationStatuses();
                }
            });
    }

    ngOnDestroy(): void {
        this.searchFilterSubscription.unsubscribe();
        this.searchTermSubscription.unsubscribe();
    }

    lazyLoad(event: TableLazyLoadEvent): void {
        this.searchFilter.sortBy = event.sortField ? event.sortField as string : this.searchFilter.sortBy;
        this.searchFilter.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
        this.loadOrganizations();
    }

    onSelectItemsPerPage(itemsPerPage: number): void {
        this.searchFilter.size = itemsPerPage;
        this.clearSelection();
        this.loadOrganizations();
    }

    onChangeCurrentPage(currentPage: number): void {
        this.searchFilter.page = currentPage;
        this.loadOrganizations();
    }

    onToggleDoNotEnable(organization: OrganizationSiteDto): void {
        const currentValue = organization.migration?.doNotEnable ?? false;
        const newValue = !currentValue;

        if (!organization.migration) {
            organization.migration = { doNotEnable: newValue };
        } else {
            organization.migration.doNotEnable = newValue;
        }

        this.organizationMigrationService
            .updateDoNotEnable(organization.id, newValue)
            .subscribe({
                error: () => {
                    organization.migration!.doNotEnable = currentValue;
                }
            });
    }

    openAddToOrganizationModal(organization: OrganizationSiteDto): void {
        this.selectedOrganizationSite = organization;
        this.showAddToOrganizationModal = true;
    }

    handleOrganizationAdded(added: boolean): void {
        this.showAddToOrganizationModal = false;
        this.selectedOrganizationSite = undefined;
        if (added) {
            this.loadOrganizations();
        }
    }

    openMoveOrganizationModal(site: OrganizationSiteDto): void {
        this.selectedOrganizationSite = site;
        this.showMoveOrganizationModal = true;
    }

    handleOrganizationMoved(moved: boolean): void {
        this.showMoveOrganizationModal = false;
        this.selectedOrganizationSite = undefined;

        if (moved) {
            this.loadOrganizations();
        }
    }

    loadRelocationStatuses(): void {
        const linkedSites = this.tableData.filter(site => site.status !== null && site.status !== undefined && site.enterpriseId !== null && site.enterpriseId !== undefined);
        if (linkedSites.length === 0) {
            this.relocatingOrganizationIds = new Set();
            return;
        }

        const sitesByEnterprise = new Map<number, number[]>();
        for (const site of linkedSites) {
            const orgIds = sitesByEnterprise.get(site.enterpriseId) ?? [];
            orgIds.push(site.id);
            sitesByEnterprise.set(site.enterpriseId, orgIds);
        }

        const requests: Observable<OrganizationRelocationStatusDto[]>[] = [];
        for (const [enterpriseId, orgIds] of sitesByEnterprise) {
            requests.push(this.organizationRelocationService.getStatuses(enterpriseId, orgIds));
        }

        forkJoin(requests).subscribe({
            next: (results) => {
                const relocatingIds = new Set<number>();
                for (const statuses of results) {
                    for (const statusDto of statuses) {
                        if (relocationInProgressStatuses.has(statusDto.status)) {
                            relocatingIds.add(statusDto.organizationRelocationRequestDto.organizationId);
                        }
                    }
                }
                this.relocatingOrganizationIds = relocatingIds;
            }
        });
    }

    isRelocationInProgress(organizationId: number): boolean {
        return this.relocatingOrganizationIds.has(organizationId);
    }

    isMoveDisabled(organization: OrganizationSiteDto): boolean {
        return organization.status === OrganizationStatus.INACTIVE;
    }

    getSelectedOrganizationDto(): OrganizationDto | undefined {
        if (!this.selectedOrganizationSite) {
            return;
        }

        const site = this.selectedOrganizationSite;

        return {
            id: site.id,
            name: site.organizationName,
            urlPrefix: site.organizationUrl,
            globalIdentifier: site.globalIdentifier,
            internalId: site.internalId,
            pod: site.pod
        };
    }

    get displaySelectionBanner(): boolean {
        return this.allPagesSelected || this.selectedIds.size > 0;
    }

    get displayCount(): number {
        return this.allPagesSelected ? this.totalItems : this.selectedIds.size;
    }

    isSelectableForAnyAction(item: OrganizationSiteDto): boolean {
        return !item.enterpriseLoginActive;
    }

    isPreconfigureEligible(item: OrganizationSiteDto): boolean {
        return item.enterpriseId === null || item.enterpriseId === undefined;
    }


    isCurrentPageAllSelected(): boolean {
        const selectable = this.tableData?.filter(item => this.isSelectableForAnyAction(item)) ?? [];
        return this.allPagesSelected || (selectable.length > 0 && selectable.every(item => this.selectedIds.has(item.id)));
    }

    toggleCurrentPage(checked: boolean): void {
        if (this.allPagesSelected && !checked) {
            this.allPagesSelected = false;
        }
        this.tableData?.forEach(item => {
            if (checked) {
                if (this.isSelectableForAnyAction(item)) {
                    this.selectedIds.add(item.id);
                    if (this.isPreconfigureEligible(item)) {
                        this.preconfigureEligibleSelectedIds.add(item.id);
                    }
                }
            } else {
                this.selectedIds.delete(item.id);
                this.preconfigureEligibleSelectedIds.delete(item.id);
            }
        });
    }

    toggleRow(item: OrganizationSiteDto, checked: boolean): void {
        if (!this.isSelectableForAnyAction(item)) return;
        if (this.allPagesSelected) {
            this.allPagesSelected = false;
            this.tableData?.forEach(i => {
                if (this.isSelectableForAnyAction(i)) {
                    this.selectedIds.add(i.id);
                    if (this.isPreconfigureEligible(i)) {
                        this.preconfigureEligibleSelectedIds.add(i.id);
                    }
                }
            });
        }
        if (checked) {
            this.selectedIds.add(item.id);
            if (this.isPreconfigureEligible(item)) {
                this.preconfigureEligibleSelectedIds.add(item.id);
            }
        } else {
            this.selectedIds.delete(item.id);
            this.preconfigureEligibleSelectedIds.delete(item.id);
        }
    }

    selectAllInFilter(): void {
        this.allPagesSelected = true;
    }

    clearSelection(): void {
        this.allPagesSelected = false;
        this.selectedIds.clear();
        this.preconfigureEligibleSelectedIds.clear();
    }

    asCheckbox(event: Event): HTMLInputElement {
        return event.target as HTMLInputElement;
    }

    openBulkActionsModal(): void {
        if (!this.allPagesSelected && this.selectedIds.size === 0) {
            return;
        }
        const eligibleIds = this.getPreconfigureEligibleIds();
        this.canSetPreconfigureDate = this.allPagesSelected || eligibleIds.length > 0;
        this.canSetEnablementDate = this.resolveCanSetEnablementDate();
        this.preconfigureFiltersSnapshot = { ...this.searchFilter };
        this.existingPreconfigureDate = this.resolveInitialPreconfigureDate(eligibleIds);
        this.existingEnablementDate = this.resolveInitialEnablementDate();
        this.preconfigureExcludedCount = this.allPagesSelected ? 0 : this.selectedIds.size - eligibleIds.length;
        this.showPreconfigureModal = true;
    }

    private getPreconfigureEligibleIds(): number[] {
        return Array.from(this.preconfigureEligibleSelectedIds);
    }

    private revalidateEligibilityForCurrentPage(): void {
        for (const item of this.tableData) {
            if (!this.isPreconfigureEligible(item)) {
                this.preconfigureEligibleSelectedIds.delete(item.id);
            }
        }
    }

    private parseDateArray(parts: number[] | null | undefined): Date | null {
        if (!parts || parts.length < 3) return null;
        const [year, month, day] = parts;
        return new Date(year, month - 1, day);
    }

    private resolveInitialPreconfigureDate(eligibleIds: number[]): Date | null {
        if (this.allPagesSelected) return null;
        let siteId: number | null = null;
        if (eligibleIds.length === 1) {
            siteId = eligibleIds[0];
        } else if (eligibleIds.length === 0 && this.selectedIds.size === 1) {
            siteId = Array.from(this.selectedIds)[0];
        }
        if (siteId === null) return null;
        return this.parseDateArray(this.tableData?.find(s => s.id === siteId)?.migration?.scheduledPreconfigDate);
    }

    private resolveInitialEnablementDate(): Date | null {
        if (this.allPagesSelected || this.selectedIds.size !== 1) return null;
        const siteId = Array.from(this.selectedIds)[0];
        return this.parseDateArray(this.tableData?.find(s => s.id === siteId)?.migration?.scheduledEnableDate);
    }

    private resolveCanSetEnablementDate(): boolean {
        if (this.allPagesSelected || this.selectedIds.size !== 1) return true;
        const siteId = Array.from(this.selectedIds)[0];
        const site = this.tableData?.find(s => s.id === siteId);
        return !site?.enterpriseLoginActive;
    }

    handleBulkActionsSaved(saved: boolean): void {
        this.showPreconfigureModal = false;
        if (saved) {
            this.clearSelection();
            this.loadOrganizations();
        }
    }

    onBulkActionsModalClose(): void {
        this.showPreconfigureModal = false;
    }

    get selectedSiteIds(): number[] {
        if (this.allPagesSelected) return [];
        return Array.from(this.selectedIds);
    }

    private getDefaultFilter(): OrganizationSiteFilterDto {
        return {
            page: 1,
            size: 10,
            sortBy: 'name',
            sortOrder: 'ASC'
        };
    }
}
